(ns frontend.ui.component.date-range-picker.view
  (:require
   ["react-date-range" :refer (DateRangePicker)]
   [clojure.string :as string]
   [clojure.walk :refer [keywordize-keys]]
   [common.localization :as loc]
   [frontend.bootstrap.components :as rbs]
   [frontend.state.current-session :as session]
   [frontend.ui.component.date-range-picker.style :as style]
   [frontend.ui.component.icon.view :refer [icon]]
   [frontend.utils :as fu]
   [herb.core :refer [<class]]
   [reagent.core :as r]))

(def selection-ranges

  (clj->js {:start-date (js/Date.)
            :end-date   (js/Date.)
            :key        "selection"}))

(def selection-ranges-2

  (clj->js {:start-date (js/Date.)
            :end-date   (js/Date.)
            :key        "Second"}))

(defn date-range-picker
  [_args]
  (fn [args]
    [:> DateRangePicker
     (merge {:months              1
             :range-colors        ["#92C551"]
             :color               "#92C551"
             :direction           "horizontal"
             :min-date            (js/Date. 1435744977000) ; 2015
             :max-date            (js/Date.)               ; Today is the last day you can select?
             :static-input-ranges []}
            args)]))


(defn js-date->utc-js-date
  [dt]
  (when dt
    (let [ms-value (.getTime dt)
          offset (* 60 1000 (.getTimezoneOffset dt))
          utc-ms (- ms-value offset)]

      ;; zapflow stores all dates in epoch millis 00:00:00.000 in UTC
      (js/Date. utc-ms))))

(defn utc-js-date->local-date
  [dt]
  (when dt
    (let [ms-value (.getTime dt)
          offset (* 60 1000 (.getTimezoneOffset dt))
          utc-ms (+ ms-value offset)
          res (js/Date. utc-ms)]
      res)))

(defn now [] (js/Date. (js/Date.now)))
(defn end-of-today-utc [] (.setUTCHours (js/Date.) 23 59 59 0))

(defn start-of-day-utc [date] (js/Date. (.setUTCHours date 0 0 0 0)))
(defn end-of-day-utc [date] (js/Date. (.setUTCHours date 23 59 59 0)))

(defn twelve-months-ago
  []
  (let [d (js/Date. (js/Date.now))
        previous-year (- (.getFullYear d) 1)
        new-date (.setFullYear d previous-year)]
    (js/Date. new-date)))

#_(defn beginning-of-year [] (js/Date. (js/Date.UTC (.getFullYear (now)) 0 1 0 0 0 0)))

(defn beginning-of-year
  ([] (beginning-of-year (.getFullYear (now))))
  ([year] (js/Date. (js/Date.UTC year 0 1 0 0 0 0))))

(defn end-of-year
  ([] (end-of-year (.getFullYear (now))))
  ([year] (js/Date. (js/Date.UTC year 11 31 23 59 59 0))))

(defn beginning-of-month
  []
  (js/Date. (js/Date.UTC (.getFullYear (now)) (.getMonth (now)) 1 0 0 0 0)))

(defn get-quarter
  "Returns a list (start-date, end-date)"
  [quarter year]
  (condp = quarter
    1 (list (js/Date. (js/Date.UTC year 0 1 0 0 0)) (js/Date. (js/Date.UTC year 2 31 23 59 59)))
    2 (list (js/Date. (js/Date.UTC year 3 1 0 0 0)) (js/Date. (js/Date.UTC year 5 30 23 59 59)))
    3 (list (js/Date. (js/Date.UTC year 6 1 0 0 0)) (js/Date. (js/Date.UTC year 8 30 23 59 59)))
    4 (list (js/Date. (js/Date.UTC year 9 1 0 0 0)) (js/Date. (js/Date.UTC year 11 31 23 59 59)))
  ))

(defn current-quarter-number
  []
  (let [current-month (.getMonth (now))]
    (cond
      (<= 0 current-month 2) 1
      (<= 3 current-month 5) 2
      (<= 6 current-month 8) 3
      (<= 9 current-month 11) 4)))

(defn get-nth-quarter-of-year
  [nth-quarter]
  (get-quarter nth-quarter (.getFullYear (now))))

(defn get-previous-quarter
  []
  (let [current-q (current-quarter-number)
        current-year (.getFullYear (now))]
    (if (= current-q 1)
      (get-quarter 4 (- current-year 1))                    ;; last year's final quarter
      (get-quarter (- current-q 1) current-year))))         ;; Previous quarter this year

(defn this-quarter-to-date
  []
  (let [now (now)
        [start-date _] (get-quarter (current-quarter-number) (.getFullYear now))
        end-date (end-of-day-utc now)]
    [start-date end-date]))

(defn from-2018-to-today
  []
  (let [now (now)
        start-date (js/Date. 1514764800000)
        end-date (end-of-day-utc now)]
    [start-date end-date]))

(defn get-past-8-quarters
  []
  (let [now (now)
        current-year (.getFullYear now)
        current-q-number (current-quarter-number)
        three-years-quarters (for [x (range 1 13)
                                   :let [q (condp < x
                                             8 {:quarter (- x 8)
                                                :year    current-year}
                                             4 {:quarter (- x 4)
                                                :year    (- current-year 1)}
                                             {:quarter x
                                              :year    (- current-year 2)})]]
                               q)
        rev (reverse three-years-quarters)
        drop-qs (- 5 current-q-number)
        ans (->> (drop drop-qs rev)
                 (take 8))]
    ans))

(defn last-8-quarter-dates
  []
  (let [past-8 (get-past-8-quarters)
        past-8-dates (->> past-8
                          (mapv (fn [{:keys [quarter year]}]
                                  (let [[start-date end-date] (get-quarter quarter year)]
                                    {:startDate start-date
                                     :endDate   end-date
                                     :key       "selection"
                                     :range-kw  (keyword (str "q-" quarter "-" year))}))))]
    past-8-dates))

(defn n-years-back
  [n local-atom]
  (let [now (now)
        current-year (.getFullYear now)]
    (->> (range n)
         (mapv (fn [num]
                 (let [year (- current-year num)
                       start (beginning-of-year year)
                       end (if (= current-year year)
                             (end-of-day-utc now)
                             (end-of-year year))
                       range-key (keyword (str year))]
                   {:label      (str year)
                    :range      (fn []
                                  {:startDate (utc-js-date->local-date start)
                                   :endDate   (utc-js-date->local-date end)
                                   :key       range-key})
                    :isSelected (fn []
                                  (= (:range-kw @local-atom) range-key))}))))))

(defn get-default-static-ranges
  [local-atom]
  (let [last-8-q (last-8-quarter-dates)
        last-12-months (list (twelve-months-ago) (end-of-day-utc (now)))
        year-to-date (list (beginning-of-year) (end-of-day-utc (now)))
        this-q-td (this-quarter-to-date)
        eod-today (last year-to-date)
        now2 (now)
        now (now)
        thirty-days-ago (-> (.setDate now (- (.getDate now) 30))
                            js/Date.
                            start-of-day-utc)
        ninety-days-ago (-> (.setDate now2 (- (.getDate now2) 90))
                            js/Date.
                            start-of-day-utc)
        ranges-past-4-quarters
        (->> last-8-q
             (take 4)
             (mapv (fn [{:keys [startDate endDate _key range-kw]}]
                     (let [[_ q year] (string/split (str range-kw) #"-")
                           label (str "Q" q " " year)]
                       {:label      label
                        :range      (fn []
                                      {:startDate (utc-js-date->local-date startDate)
                                       :endDate   (utc-js-date->local-date endDate)
                                       :key       range-kw})
                        :isSelected (fn []
                                      (= (:range-kw @local-atom) range-kw))}))))
        range-ltm {:label      "Last 12 months"
                   :range      (fn []
                                 {:startDate (utc-js-date->local-date (first last-12-months))
                                  :endDate   (utc-js-date->local-date (last last-12-months))
                                  :key       :ltm})
                   :isSelected (fn []
                                 (= (:range-kw @local-atom) :ltm))}
        range-ytd {:label      "Year to date"
                   :range      (fn []
                                 {:startDate (utc-js-date->local-date (first year-to-date))
                                  :endDate   (utc-js-date->local-date (last year-to-date))
                                  :key       :ytd})
                   :isSelected (fn []
                                 (= (:range-kw @local-atom) :ytd))}
        range-current-fiscal-quarter
        {:label      "Current quarter to date"
         :range      (fn []
                       {:startDate (utc-js-date->local-date (first this-q-td))
                        :endDate   (utc-js-date->local-date (last this-q-td))
                        :key       :current-q-td})
         :isSelected (fn []
                       (= (:range-kw @local-atom) :current-q-td))}
        last-30-days {:label      "Last 30 days"
                      :range      (fn []
                                    {:startDate (utc-js-date->local-date thirty-days-ago)
                                     :endDate   (utc-js-date->local-date eod-today)
                                     :key       :last-30-days})
                      :isSelected (fn []
                                    (= (:range-kw @local-atom) :last-30-days))}

        last-90-days {:label      "Last 90 days"
                      :range      (fn []
                                    {:startDate (utc-js-date->local-date ninety-days-ago)
                                     :endDate   (utc-js-date->local-date eod-today)
                                     :key       :last-90-days})
                      :isSelected (fn []
                                    (= (:range-kw @local-atom) :last-90-days))}

        month-to-date {:label      "Month to date"
                       :range      (fn []
                                     {:startDate (utc-js-date->local-date (beginning-of-month))
                                      :endDate   (utc-js-date->local-date eod-today)
                                      :key       :month-to-date})
                       :isSelected (fn []
                                     (= (:range-kw @local-atom) :month-to-date))}

        all-ranges (concat [last-30-days
                            month-to-date
                            last-90-days
                            range-current-fiscal-quarter
                            range-ytd range-ltm]
                           ranges-past-4-quarters)]
    all-ranges))

(defn get-alj-static-ranges
  [local-atom]
  (let [this-q-td (this-quarter-to-date)
        range-all-time
        {:label      "All time"
         :range      (fn []
                       {:startDate 1514764800000
                        :endDate   (utc-js-date->local-date (last this-q-td))
                        :key       :all-time})
         :isSelected (fn []
                       (= (:range-kw @local-atom) :all-time))}
        all-ranges (concat [range-all-time]
                           (n-years-back 3 local-atom)
                           (get-default-static-ranges local-atom))]
    all-ranges))

(defn date-range-dropdown
  "Date range selector with pre-defined ranges.

  Args {:date-state-atom @atom
        :on-change fn
        :static-ranges-kw (:alj | :default)}

  date-state-atom should be in the following format:

  {:start-ts time-stamp-utc
  :end-ts time-stamp-utc
  :range-kw (predefined | :custom)}


  label is not mandatory"
  [{:keys [date-state-atom label on-change static-ranges-kw hide-label?]}]
  (let [show? (r/atom false)
        changes? (r/atom false)
        local-atom (r/atom {:startDate nil
                            :endDate   nil
                            :key       "selection"
                            :range-kw  (or (:range-kw @date-state-atom) :custom)})

        close-dropdown (fn []
                         (reset! changes? false)
                         (swap! show? not))
        handle-apply (fn []
                       (let [start-date (:startDate @local-atom)
                             end-date (:endDate @local-atom)
                             start-utc-date (js-date->utc-js-date start-date)
                             end-utc-date (js-date->utc-js-date end-date)
                             _ (.setUTCHours end-utc-date 23 59 59)
                             start-ts (.getTime start-utc-date)
                             end-ts (.getTime end-utc-date)
                             range-kw (:range-kw @local-atom)]
                         (swap! date-state-atom assoc :start-ts start-ts)
                         (swap! date-state-atom assoc :end-ts end-ts)
                         (swap! date-state-atom assoc :range-kw range-kw)
                         (when (fn? on-change)
                           (on-change))
                         (close-dropdown)))]
    (fn []
      (let [static-ranges (condp = static-ranges-kw
                            :alj (get-alj-static-ranges local-atom)
                            (get-default-static-ranges local-atom))]
        [:div
         (when-not hide-label?
           [:label.field-editor__content__header__label__text-content
            (or label (loc/tr :time-frame))])
         [rbs/Dropdown
          {:on-toggle (fn [_is-open? _event metadata]
                        (when (and (= (.-source metadata) "click")
                                   (not @show?))
                          ; Refresh local-atom state when opening drop down
                          (let [start-ts-utc (:start-ts @date-state-atom)
                                end-ts-utc (:end-ts @date-state-atom)
                                range-kw (:range-kw @date-state-atom)
                                start-date (js/Date. start-ts-utc)
                                end-date (js/Date. end-ts-utc)
                                start-transform (utc-js-date->local-date start-date)
                                end-transform (utc-js-date->local-date end-date)]
                            (reset! local-atom {:startDate start-transform
                                                :endDate   end-transform
                                                :key       "selection"
                                                :range-kw  range-kw})))
                        (when (or
                               (and
                                (= (.-source metadata) "click")
                                (not @show?))
                               (= (.-source metadata) "rootClose"))
                          (close-dropdown)))
           :show      @show?}
          [rbs/Dropdown-Toggle
           {:class (<class style/input-button)}
           [:div.d-flex.align-items-center
            [:div {:style {:margin-right "7px"}}
             (r/as-element [icon :calendar])]
            (fu/format-date-ts (:start-ts @date-state-atom) "." true)
            "  -  "
            (fu/format-date-ts (:end-ts @date-state-atom) "." true)
            [:i.fa.fa-caret-down.ml-3]]]
          [rbs/Dropdown-Menu
           [rbs/DropdownItem
            {:href  "#"
             :style {:padding "0px"}}
            [:div {:style {:background-color "white"}}
             [date-range-picker
              {:start-date-placeholder (loc/tr :from-date)
               :end-date-placeholder (loc/tr :to-date)
               :move-range-on-first-selection false
               :retain-end-date-on-first-selection false
               :ranges [(clj->js @local-atom)]
               :on-change (fn [item]
                            (reset! changes? true)
                            #_(set! *warn-on-infer* false)
                            (let [selection (.-selection item)
                                  arr (.. item -selection -arr)]
                              (if arr
                                (let [start-date (js/Date. (nth arr 1))
                                      end-date (js/Date. (nth arr 3))
                                      key (nth arr 5)]
                                  (swap! local-atom assoc :startDate start-date)
                                  (swap! local-atom assoc :endDate end-date)
                                  (swap! local-atom assoc :range-kw key))
                                (let [keywordized (keywordize-keys (js->clj selection))]
                                  (swap! local-atom assoc :range-key :custom)
                                  (reset! local-atom keywordized)))))

               :static-ranges static-ranges
               :input-ranges []
               :date-display-format (or (get @session/tenant :tenant/date-format) "d.M.yyyy")}]
              [:div.d-flex.justify-content-end {:style {:margin "0px 20px 10px 0px"}}
              [:button.btn.zap-btn__hollow.btn-sm
               {:on-click close-dropdown}
               (loc/tr :cancel)]
              [:button.btn.zap-btn__solid__green.btn-sm
               {:disabled (not @changes?)
                :on-click handle-apply}
               (loc/tr :apply)]]]]]]]))))
